import React, { createContext, useContext, useRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { findChildByType } from '@/components/ui/ui.utils';
import cn from '@/utils/cn';
import Dropdown, { IWidth } from './Dropdown';

// AIDEV-NOTE: Opinionated TCI wrapper around the Radix DropdownMenu, parallel to ui/Tooltip/TCITooltip.
// Use this for any "click trigger → list of menu actions" UI.
//
// Why this isn't a thin wrapper around Radix's MenuItem: Radix's DropdownMenu.Item calls
// `event.currentTarget.blur()` in its built-in onPointerLeave handler. That blur kills any
// <TCITooltip> wrapping a menuitem — the moment the cursor leaves the item, the trigger blurs,
// the tooltip sees focus-loss, and the content unmounts before the user can mouse into it.
//
// We keep Radix's Root/Trigger/Portal/Content (positioning, click-outside, escape, focus-restore)
// but render plain <div role="menuitem"> items ourselves, hand-rolling keyboard support. This loses
// Radix's typeahead — no consumer uses it.

interface TCIDropdownContextValue {
  close: () => void;
}

const TCIDropdownContext = createContext<TCIDropdownContextValue>({ close: () => {} });

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const queryMenuItems = (root: HTMLElement | null) => (
  root ? Array.from(root.querySelectorAll<HTMLElement>('[role="menuitem"]')) : []
);

// AIDEV-NOTE: WAI-ARIA menu pattern says Tab inside a menu should close it and move focus to the
// next/previous page tab stop. Radix's modal=true traps Tab; modal=false lets focus slip out
// silently while the menu stays open. Neither is the spec, so we hand-roll: on Tab, find the
// trigger's next/previous focusable in DOM order (skipping anything inside an open menu), close
// the dropdown, and focus that element.
const handleTabClose = (
  event: React.KeyboardEvent,
  trigger: HTMLElement | null,
  close: () => void,
) => {
  if (event.key !== 'Tab') return;
  if (!trigger) return;

  const focusable = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter(el => !el.closest('[role="menu"]'));
  const triggerIndex = focusable.indexOf(trigger);
  if (triggerIndex === -1) return;

  const next = event.shiftKey ? focusable[triggerIndex - 1] : focusable[triggerIndex + 1];
  if (!next) return;

  event.preventDefault();
  close();
  // Defer focus so it lands after Radix's onCloseAutoFocus restoration to the trigger.
  setTimeout(() => next.focus(), 0);
};

interface TCIDropdownProps {
  align?: 'start' | 'center' | 'end';
  children: ReactChildren;
  contentClassName?: string;
  modal?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  width?: IWidth;
}

const TCIDropdownComponent = React.forwardRef<
React.ElementRef<typeof Dropdown.Trigger>,
TCIDropdownProps
>(({
  align = 'end',
  children,
  contentClassName,
  modal = false,
  onOpenChange,
  open,
  side = 'bottom',
  sideOffset = 4,
  width = 'default',
}, ref) => {
  const contentChild = findChildByType(children, Dropdown.Content);
  const triggerChild = findChildByType(children, Dropdown.Trigger);

  if (!contentChild || !triggerChild) {
    throw new Error('TCIDropdown must have both TCIDropdown.Content and TCIDropdown.Trigger children');
  }

  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = open !== undefined;
  const handleOpenChange = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const close = () => handleOpenChange(false);

  type TriggerEl = React.ElementRef<typeof Dropdown.Trigger>;
  const triggerRef = useRef<TriggerEl | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const setTriggerRef = (node: TriggerEl | null) => {
    triggerRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  const setContentRef = (node: HTMLDivElement | null) => {
    contentRef.current = node;
    const incoming = (contentChild as React.ReactElement).props.ref;
    if (typeof incoming === 'function') incoming(node);
    else if (incoming) incoming.current = node;
  };

  const focusItem = (index: number) => {
    const items = queryMenuItems(contentRef.current);
    if (items.length === 0) return;
    const wrapped = (index + items.length) % items.length;
    items[wrapped].focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    contentChild.props.onKeyDown?.(e);
    if (e.defaultPrevented) return;

    handleTabClose(e, triggerRef.current, close);
    if (e.defaultPrevented) return;

    const items = queryMenuItems(contentRef.current);
    const currentIndex = items.indexOf(document.activeElement as HTMLElement);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusItem(currentIndex + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusItem(currentIndex - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusItem(0);
        break;
      case 'End':
        e.preventDefault();
        focusItem(items.length - 1);
        break;
      default:
        break;
    }
  };

  const handleOpenAutoFocus = (e: Event) => {
    contentChild.props.onOpenAutoFocus?.(e);
    if (e.defaultPrevented) return;
    e.preventDefault();
    // Defer one frame so the Content + items have rendered before we focus.
    requestAnimationFrame(() => {
      const items = queryMenuItems(contentRef.current);
      const currentIndex = items.findIndex(item => item.getAttribute('aria-current') === 'true');
      focusItem(currentIndex >= 0 ? currentIndex : 0);
    });
  };

  return (
    <TCIDropdownContext.Provider value={{ close }}>
      <Dropdown modal={modal} open={isControlled ? open : internalOpen} onOpenChange={handleOpenChange}>
        <Dropdown.Trigger ref={setTriggerRef} {...triggerChild.props}>
          {triggerChild.props.children}
        </Dropdown.Trigger>
        <Dropdown.Content
          {...contentChild.props}
          ref={setContentRef}
          align={contentChild.props.align ?? align}
          className={contentClassName ?? contentChild.props.className}
          side={contentChild.props.side ?? side}
          sideOffset={contentChild.props.sideOffset ?? sideOffset}
          width={contentChild.props.width ?? width}
          onKeyDown={handleKeyDown}
          onOpenAutoFocus={handleOpenAutoFocus}
        >
          {contentChild.props.children}
        </Dropdown.Content>
      </Dropdown>
    </TCIDropdownContext.Provider>
  );
});

TCIDropdownComponent.displayName = 'TCIDropdown';

// AIDEV-NOTE: Custom Item — does NOT delegate to DropdownMenu.Item. Renders a plain element
// with role="menuitem" + tabIndex=-1 so our keyboard handler can drive focus without Radix's
// blur-on-pointerleave killing tooltip-wrapped items. Calls onSelect on click; preventDefault
// on the synthetic select event keeps the menu open (matching Radix's onSelect API).
interface TCIDropdownItemProps extends Omit<React.HTMLAttributes<HTMLElement>, 'onSelect'> {
  asChild?: boolean;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onSelect?: (event: Event) => void;
}

const itemClasses = [
  'tw-flex tw-items-center tw-gap-2 tw-mx-1 tw-rounded tw-px-5 tw-py-1 tw-text-sm tw-text-very-dark-grey',
  'tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-left tw-no-underline tw-outline-none',
  // asChild items ARE the <a> — suppress the global a:hover/a:focus underline on the item itself
  'hover:tw-no-underline focus:tw-no-underline',
  'focus:tw-bg-light-grey',
  'focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-tci-purple',
  'aria-disabled:tw-opacity-50 aria-disabled:tw-cursor-not-allowed',
  '[&_button]:tw-bg-transparent [&_button]:tw-border-none [&_button]:tw-p-0 [&_button]:tw-w-full',
  '[&_button]:tw-text-left [&_button]:tw-cursor-pointer [&_button]:tw-text-inherit',
  '[&_a]:tw-text-inherit [&_a]:tw-no-underline hover:[&_a]:tw-no-underline [&_a]:tw-w-full [&_a]:tw-block',
  '[&_i]:tw-text-tci-purple [&_i]:tw-text-lg [&_i]:tw-mr-2',
].join(' ');

const TCIDropdownItem = React.forwardRef<HTMLElement, TCIDropdownItemProps>(({
  asChild,
  children,
  className,
  disabled,
  onClick,
  onPointerEnter,
  onSelect,
  ...rest
}, ref) => {
  const { close } = useContext(TCIDropdownContext);
  const Comp: React.ElementType = asChild ? Slot : 'div';

  // Highlight follows both pointer and keyboard through a single state (focus),
  // so hovering one item while arrowing to another can't show two highlights.
  const handlePointerEnter = (e: React.PointerEvent<HTMLElement>) => {
    onPointerEnter?.(e);
    e.currentTarget.focus({ preventScroll: true });
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
    if (e.defaultPrevented) return;

    if (onSelect) {
      const selectEvent = new CustomEvent('tcidropdown.select', { cancelable: true });
      onSelect(selectEvent);
      if (selectEvent.defaultPrevented) return;
    }

    close();
  };

  // preventDefault swaps the element's native default (link nav, Space page-scroll) for one synthesized
  // click, so plain <div> and asChild <a> items activate identically.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    if (disabled || e.currentTarget.getAttribute('aria-disabled') === 'true') return;
    e.currentTarget.click();
  };

  return (
    <Comp
      ref={ref as React.Ref<HTMLDivElement>}
      aria-disabled={disabled || undefined}
      className={cn(itemClasses, className)}
      role="menuitem"
      tabIndex={-1}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onPointerEnter={handlePointerEnter}
      {...rest}
    >
      {children}
    </Comp>
  );
});

TCIDropdownItem.displayName = 'TCIDropdown.Item';

const TCIDropdownContent = Dropdown.Content;
TCIDropdownContent.displayName = 'TCIDropdown.Content';

const TCIDropdownTrigger = Dropdown.Trigger;
TCIDropdownTrigger.displayName = 'TCIDropdown.Trigger';

const TCIDropdownSeparator = Dropdown.Separator;
TCIDropdownSeparator.displayName = 'TCIDropdown.Separator';

const TCIDropdownLabel = Dropdown.Label;
TCIDropdownLabel.displayName = 'TCIDropdown.Label';

const TCIDropdownGroup = Dropdown.Group;
TCIDropdownGroup.displayName = 'TCIDropdown.Group';

const TCIDropdownPortal = Dropdown.Portal;
TCIDropdownPortal.displayName = 'TCIDropdown.Portal';

const TCIDropdown = Object.assign(TCIDropdownComponent, {
  Content: TCIDropdownContent,
  Group: TCIDropdownGroup,
  Item: TCIDropdownItem,
  Label: TCIDropdownLabel,
  Portal: TCIDropdownPortal,
  Separator: TCIDropdownSeparator,
  Trigger: TCIDropdownTrigger,
});

export default TCIDropdown;
